import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  Euro,
  SquareFoot,
  CalendarToday,
  AccessTime,
  Email,
  CheckCircle,
  Cancel,
  Home,
  Wifi,
  LocalParking,
  Pets,
  SmokingRooms,
  Balcony
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../lib/config';
import { Listing } from '../types';
import { getListingImage } from '../lib/images';

const ListingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [contactErrors, setContactErrors] = useState<{
    name?: string;
    email?: string;
    message?: string;
  }>({});
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitError, setContactSubmitError] = useState<string | null>(null);
  const [contactSubmitSuccess, setContactSubmitSuccess] = useState(false);
  const imageUrl = getListingImage(listing);

  useEffect(() => {
    const fetchListing = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/wg/listings/${id}`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setListing(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch listing');
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const resetContactState = () => {
    setContactForm({ name: '', email: '', message: '' });
    setContactErrors({});
    setContactSubmitError(null);
    setContactSubmitSuccess(false);
  };

  const handleContactOpen = () => {
    resetContactState();
    setIsContactOpen(true);
  };

  const handleContactClose = () => {
    if (contactSubmitting) {
      return;
    }

    resetContactState();
    setIsContactOpen(false);
  };

  const validateContactForm = () => {
    const errors: { name?: string; email?: string; message?: string } = {};
    const trimmedName = contactForm.name.trim();
    const trimmedEmail = contactForm.email.trim();
    const trimmedMessage = contactForm.message.trim();

    if (!trimmedName) {
      errors.name = t('listings.contactForm.errors.nameRequired');
    }

    if (!trimmedEmail) {
      errors.email = t('listings.contactForm.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      errors.email = t('listings.contactForm.errors.emailInvalid');
    }

    if (!trimmedMessage) {
      errors.message = t('listings.contactForm.errors.messageRequired');
    }

    setContactErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleContactChange = (
    field: 'name' | 'email' | 'message'
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;

    setContactForm((prev) => ({
      ...prev,
      [field]: value
    }));

    if (contactErrors[field]) {
      setContactErrors((prev) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleContactSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!id) {
      return;
    }

    if (!validateContactForm()) {
      return;
    }

    const payload = {
      name: contactForm.name.trim(),
      email: contactForm.email.trim(),
      message: contactForm.message.trim()
    };

    try {
      setContactSubmitting(true);
      setContactSubmitError(null);

      const response = await fetch(`${API_BASE_URL}/wg/listings/${id}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = t('listings.contactForm.errors.submitFailed');

        try {
          const errorData = await response.json();

          if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // ignore JSON parse errors
        }

        throw new Error(errorMessage);
      }

      setContactSubmitSuccess(true);
      setContactForm(payload);
    } catch (err) {
      setContactSubmitError(
        err instanceof Error
          ? err.message
          : t('listings.contactForm.errors.submitFailed')
      );
    } finally {
      setContactSubmitting(false);
    }
  };

  const baseLanguage = i18n.language?.split('-')[0] ?? 'en';
  const flexibleFallbackMap: Record<string, string> = {
    de: 'Flexibel',
    en: 'Flexible',
    fr: 'Flexible',
    es: 'Flexible',
  };
  const flexibleFallbackText = flexibleFallbackMap[baseLanguage] ?? 'Flexible';

  const formatAvailableFrom = (value: string | null | undefined) => {
    if (!value) {
      return t('listings.flexibleAvailability', flexibleFallbackText);
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return t('listings.flexibleAvailability', flexibleFallbackText);
    }

    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(parsedDate);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !listing) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Listing not found'}
        </Alert>
        <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBack />}>
          {t('common.back')}
        </Button>
      </Container>
    );
  }

  const trimmedDistrict = listing.district?.trim();
  const locationLabel = trimmedDistrict ? `${listing.city} • ${trimmedDistrict}` : listing.city;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleBack}
        startIcon={<ArrowBack />}
        sx={{ mb: 3, color: 'primary.contrastText' }}
      >
        {t('common.back')}
      </Button>

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Image */}
          <Card sx={{ mb: 3 }}>
            <CardMedia
              component="img"
              height="400"
              image={imageUrl}
              alt={listing.title}
              sx={{ objectFit: 'cover' }}
            />
          </Card>

          {/* Title and Basic Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom>
                {listing.title}
              </Typography>
              
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip
                  icon={<LocationOn />}
                  label={locationLabel}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<Euro />}
                  label={`${listing.price}€`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<SquareFoot />}
                  label={`${listing.size} m²`}
                  color="info"
                  variant="outlined"
                />
                <Chip
                  icon={listing.furnished ? <CheckCircle /> : <Cancel />}
                  label={listing.furnished ? t('listings.furnished') : t('listings.unfurnished')}
                  color={listing.furnished ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              <Typography variant="h6" gutterBottom>
                {t('listings.description')}
              </Typography>
              <Typography variant="body1" paragraph>
                {listing.description}
              </Typography>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('listings.details')}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.availableFrom')}
                    secondary={formatAvailableFrom(listing.availableFrom)}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <AccessTime />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.duration')}
                    secondary={listing.duration}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.contact')}
                    secondary={listing.contactEmail}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Contact Card */}
          <Card sx={{ mb: 3, position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('listings.contact')}
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleContactOpen}
                  startIcon={<Email />}
                  size="large"
                >
                  {t('listings.contactNow')}
                </Button>
                
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('listings.contactInfo')}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Features */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('listings.features')}
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Home />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.roomSize')}
                    secondary={`${listing.size} m²`}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    {listing.furnished ? <CheckCircle color="success" /> : <Cancel color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.furnished')}
                    secondary={listing.furnished ? t('common.yes') : t('common.no')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Wifi />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.wifi')}
                    secondary={t('common.included')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <LocalParking />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.parking')}
                    secondary={t('listings.parkingAvailable')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Pets />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.pets')}
                    secondary={t('listings.petsAllowed')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <SmokingRooms />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.smoking')}
                    secondary={t('listings.smokingAllowed')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Balcony />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('listings.balcony')}
                    secondary={t('listings.balconyAvailable')}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={isContactOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && contactSubmitting) {
            return;
          }
          handleContactClose();
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleContactSubmit} noValidate>
          <DialogTitle>{t('listings.contactForm.title')}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('listings.contactForm.subtitle')}
            </Typography>

            {contactSubmitSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('listings.contactForm.success')}
              </Alert>
            )}

            {contactSubmitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {contactSubmitError}
              </Alert>
            )}

            <TextField
              label={t('listings.contactForm.nameLabel')}
              value={contactForm.name}
              onChange={handleContactChange('name')}
              fullWidth
              margin="normal"
              disabled={contactSubmitting || contactSubmitSuccess}
              error={Boolean(contactErrors.name)}
              helperText={contactErrors.name}
              required
            />

            <TextField
              label={t('listings.contactForm.emailLabel')}
              type="email"
              value={contactForm.email}
              onChange={handleContactChange('email')}
              fullWidth
              margin="normal"
              disabled={contactSubmitting || contactSubmitSuccess}
              error={Boolean(contactErrors.email)}
              helperText={contactErrors.email}
              required
            />

            <TextField
              label={t('listings.contactForm.messageLabel')}
              value={contactForm.message}
              onChange={handleContactChange('message')}
              fullWidth
              margin="normal"
              disabled={contactSubmitting || contactSubmitSuccess}
              error={Boolean(contactErrors.message)}
              helperText={contactErrors.message}
              multiline
              minRows={4}
              required
              placeholder={t('listings.contactForm.messagePlaceholder')}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleContactClose} disabled={contactSubmitting}>
              {t('listings.contactForm.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={contactSubmitting || contactSubmitSuccess}
            >
              {contactSubmitting
                ? t('listings.contactForm.submitting')
                : t('listings.contactForm.submit')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
};

export default ListingDetail;
